import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import {
    Button,
    Paper,
    Typography,
    Tooltip,
    DialogTitle,
    DialogContent,
    DialogContentText,
} from '@material-ui/core';
import {
    DeleteForever, Edit, FileCopy, Visibility, ReportProblemRounded
} from '@material-ui/icons';
import { faPlusCircle, faArchive } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Icon as MdiIcon } from '@mdi/react';
import { mdiContentDuplicate } from '@mdi/js';
import { toastr } from 'react-redux-toastr';
import { useDispatch, useSelector } from 'react-redux';
import {
    Table,
} from 'rsuite';
import './BiddingList.scss';

import _ from 'lodash';
import { dynamicSort } from '@commonClient2/services/sort';
import { useAvailableHeight } from '@commonClient2/hooks';
import {
    BidProfileStatus, BidProfileEditTab, ManagerPage, BidProcessType,
} from '@enums/index';
import { getBidProcessTypeLabel, getScheduleSourceLabel } from '@enums/Helpers/LabellingHelper';
import AppBar from '@manager/AppBar';
import {
    fetchManagerAppData, fetchPartialManagerAppData, UPDATE_MANAGER_APP_DATA,
} from '@manager/store/appData';
import {
    deleteDraft,
    deleteLatestDraftRebid,
    createBidProfileDraft,
    emptyProfileDraft,
    editBidProfileDraft,
    duplicateDraft,
} from '@manager/store/bidProfileDraft';
import {
    emptyProfile,
    deleteBidProfile,
    deleteLatestRebid,
    archiveBidProfile,
    duplicateBidProfile,
    rebidBidProfile,
    loadBidProfileEdit,
} from '@manager/store/bidProfile';

import FilterSelectionBar from './FilterSelectionBar';
import {
    formatData,
    hasCreationAccess,
    hasManagementAccess,
    hasViewAccess,
    hasRebidAccess,
    isReadOnly,
    isArchivable,
    getRequestPeriodLabel,
    getSubmissionDateLabel,
    searchableBid,
    addRebidParentsStatusToFilter,
    rebidParentsContainsStatus,
} from './BiddingList.service';
import BiddingListNameCell from './BiddingListNameCell';
import BiddingListTooltipCell from './BiddingListTooltipCell';

/** @typedef {'asc'|'desc'} SortType */
/** @type {SortType} */
const defaultSortType = 'asc';

const { Column, HeaderCell, Cell } = Table;

const rowKey = 'id';

/**
 * @param {{
 *  tooltipLabel: string } & import('@material-ui/core').ButtonProps} param0
 */
const ButtonWithTooltip = ({ tooltipLabel, ...rest }) => (
    <Tooltip title={tooltipLabel}>
        <span>
            <Button
                className="tablerow-action-button"
                {...rest}
            >
                {rest.children}
            </Button>
        </span>
    </Tooltip>
);

function BiddingList() {
    const { t } = useTranslation('shiftBidding', { keyPrefix: 'manager.biddingList' });
    const dispatch = useDispatch();

    const [sortValues, setSortValues] = useState({ sortColumn: 'name', sortType: defaultSortType });
    const { managerAppData, publicConfigs } = useSelector((/** @type {ShiftBidding.ShiftBiddingStore} */state) => state);
    const {
        bidProfileDrafts,
        bidProfiles,
        archivedBidProfiles,
        hyperfinds,
        isAppDataLoaded,
        managerBidSetups,
        pageName,
    } = managerAppData;
    const hasManagerBidSetups = managerBidSetups.filter((x) => x.canCreateBidProfile).length > 0;

    /**
     * @param {BidProcessType} bidProcessType
     * @param {number | null} setupId
     * @returns {boolean}
     */
    const hasRebidButton = (bidProcessType, setupId) => bidProcessType === BidProcessType.Preferential
        && !!managerBidSetups.find((x) => x.bidSetupId === setupId)?.isRebidAllowed;

    /** @type {[string, Function]} */
    const [searchValue, setSearchValue] = useState('');
    /** @type {[number[], Function]} */
    const [statusFilterSelected, setStatusFilterSelected] = useState(Object.values(BidProfileStatus));
    /** @type {[BidProfile.IBriefBaseProfile[], Function]} */
    // @ts-ignore
    const [filteredData, setFilteredData] = useState(bidProfileDrafts.concat(bidProfiles));
    // @ts-ignore
    const allBids = bidProfileDrafts.concat(bidProfiles);
    
    const allStatus = Object.values(BidProfileStatus).sort((a, b) => a - b);
    addRebidParentsStatusToFilter(allStatus, allBids);

    const isCreateDraftActionDisabled = useRef(managerAppData.isCreateDraftActionProcessing);

    const ref = useRef(null);
    const height = useAvailableHeight(ref) || 0;

    const handleCreateNewBid = () => {
        if (isCreateDraftActionDisabled.current) {
            return;
        }
        dispatch({ type: UPDATE_MANAGER_APP_DATA, payload: { isCreateDraftActionProcessing: true, pageName: t('createNewBid') } });
        isCreateDraftActionDisabled.current = true;
        dispatch(createBidProfileDraft(
            {
                bidProfileDrafts,
                hyperfinds,
            },
            publicConfigs.isWorkplanIntegrationAvailable
        ));
    };

    function handleFilter() {
        // @ts-ignore
        let filteredBids = bidProfileDrafts.concat(bidProfiles).sort((a, b) => b.lastUpdate - a.lastUpdate);
        if (!searchValue && !statusFilterSelected?.length) {
            setFilteredData(filteredBids);
        }
        
        // filter by search value
        if (searchValue) {
            filteredBids = filteredBids.filter((bid) => searchableBid(bid, t('anytime')).includes(searchValue.trim().toLocaleLowerCase()));
        }
        // filter by status
        if (statusFilterSelected.length) {
            filteredBids = filteredBids.filter((bid) => statusFilterSelected.includes(bid.status)
                || rebidParentsContainsStatus(bid, statusFilterSelected));
        }
        setFilteredData(filteredBids);
    }

    /** @param {BidProfile.IBriefBaseProfile} bid */
    const handleRebid = (bid) => {
        dispatch(emptyProfileDraft());
        dispatch(rebidBidProfile(
            bid.id,
            {
                bidProfiles,
                bidProfileDrafts,
                hyperfinds,
                managerBidSetups,
            },
        ));
    };

    /** @param {BidProfile.IBriefBaseProfile} bid */
    const handleEdit = (bid) => {
        switch (bid.status) {
            case BidProfileStatus.Scheduled:
            case BidProfileStatus.Active:
            case BidProfileStatus.Closed:
            case BidProfileStatus.Published:
            case BidProfileStatus.Canceled: {
                dispatch(emptyProfileDraft());
                // @ts-ignore
                const shouldShowDashboard = bid.status !== BidProfileStatus.Draft || hasManagementAccess(bid);
                dispatch(loadBidProfileEdit(
                    bid.id, 
                    {
                        hyperfinds, 
                        managerBidSetups, 
                        bidProfiles,
                    }, 
                    hasManagementAccess(bid) || hasViewAccess(bid), 
                    shouldShowDashboard ? BidProfileEditTab.Dashboard : BidProfileEditTab.Form
                ));
                break;
            }
            case BidProfileStatus.Draft:
            default:
                dispatch(emptyProfile());
                dispatch(editBidProfileDraft(bid.id, {
                    hyperfinds, managerBidSetups,
                }));
                break;
        }
    };

    /**
     * @param {BidProfile.IBriefBaseProfile} bid
     */
    const handleDuplicate = (bid) => {
        if (bid.status === BidProfileStatus.Draft) {
            dispatch(emptyProfile());
            dispatch(duplicateDraft(
                bid.id,
                {
                    bidProfileDrafts,
                    hyperfinds,
                    managerBidSetups,
                },
            ));
        } else {
            dispatch(emptyProfileDraft());
            dispatch(duplicateBidProfile(
                bid.id,
                {
                    bidProfileDrafts,
                    hyperfinds,
                    managerBidSetups,
                },
            ));
        }
    };
    /**
     * @param {BidProfile.IBriefBaseProfile} bid
    */
    const handleDelete = (bid) => {
        let deleteMiddleware = () => { };
        if (bid.rebidParents?.length) {
            if (bid.status === BidProfileStatus.Draft) {
                deleteMiddleware = () => deleteLatestDraftRebid(bid.id, {
                    bidProfiles,
                    bidProfileDrafts,
                    hyperfinds,
                });
            } else {
                deleteMiddleware = () => deleteLatestRebid(bid.id, bid.rebidParents, {
                    bidProfiles,
                    hyperfinds,
                });
            }
        } else if (bid.status === BidProfileStatus.Draft) {
            deleteMiddleware = () => deleteDraft(bid.id, bidProfileDrafts);
        } else {
            deleteMiddleware = () => deleteBidProfile(bid.id, bidProfiles);
        }
        const toastrConfirmOptions = {
            onOk: () => dispatch(deleteMiddleware()),
            onCancel: () => { },
            component: () =>
                (
                    <div>
                        <DialogTitle style={{ padding: '16px 24px' }}>
                            {t('rowActions.deleteTitle')}
                        </DialogTitle>
                        <DialogContent>
                            <DialogContentText>
                                {`${t('rowActions.deleteConfirmation')} ${bid.name || t('defaultUnnamedDraftLabel')}?`}
                            </DialogContentText>
                        </DialogContent>
                    </div>
                ),
        };
        toastr.confirm('', toastrConfirmOptions);
    };
        /**
     * @param {BidProfile.IBriefBaseProfile} bid
     */
    const handleArchive = (bid) => {
        const archiveMiddleware = () => archiveBidProfile(bid.id, bidProfiles, archivedBidProfiles);
        const toastrConfirmOptions = {
            onOk: () => dispatch(archiveMiddleware()),
            onCancel: () => { },
            component: () =>
                (
                    <div>
                        <DialogTitle style={{ padding: '16px 24px' }}>
                            {t('rowActions.archiveTitle')}
                        </DialogTitle>
                        <DialogContent>
                            <DialogContentText>
                                <div className="test">
                                    {
                                        bid.isRebidAllowed && (
                                            <ReportProblemRounded style={{ color: 'orange', paddingRight: '2px' }} />
                                        )
                                    }
                                    {`${bid.isRebidAllowed ? t('rowActions.rebidWarning') : ''}\n`}
                                </div>

                                {`${t('rowActions.archiveConfirmation')} ${bid.name}?`}
                            </DialogContentText>
                        </DialogContent>
                    </div>
                ),
        };
        toastr.confirm('', toastrConfirmOptions);
    };

    /**
     * @param {string} dataKey
     * @param {SortType} sortType
     */
    const handleSort = (dataKey, sortType) => {
        /** @type {BidProfile.IBriefBaseProfile[]} */
        let sortedFilteredData = [];
        const dynamicSortOrder = sortType === 'desc' ? '-' : '';
        const order = sortType === 'desc' ? -1 : 1;
        // eslint-disable-next-line max-len
        const nullCheckingDateSort = (/** @type {string} */ propName) => (/** @type {BidProfile.IBriefBaseProfile} */ obj1, /** @type {BidProfile.IBriefBaseProfile} */ obj2) => {
            const a = _.get(obj1, propName, null);
            const b = _.get(obj2, propName, null);
            if (!a && !b) {
                return 0;
            }
            if (!a) {
                return order * -1;
            }
            if (!b) {
                return order * 1;
            }
            const momentA = moment(a);
            const momentB = moment(b);
            // eslint-disable-next-line no-nested-ternary
            return order * ((momentA < momentB) ? -1 : (momentA > momentB) ? 1 : 0);
        };
        const nullCheckingDateRangeSort = (/** @type {string} */ prop1Name, /** @type {string} */ prop2Name) =>
            (/** @type {BidProfile.IBriefBaseProfile} */ obj1, /** @type {BidProfile.IBriefBaseProfile} */ obj2) => {
                const prop1Res = nullCheckingDateSort(prop1Name)(obj1, obj2);
                if (prop1Res !== 0) {
                    return prop1Res;
                }

                return nullCheckingDateSort(prop2Name)(obj1, obj2);
            };
        switch (dataKey) {
            case 'Name':
                sortedFilteredData = filteredData.sort(
                    dynamicSort(`${dynamicSortOrder}name`),
                );
                break;
            case 'Description':
                sortedFilteredData = filteredData.sort(
                    dynamicSort(`${dynamicSortOrder}description`),
                );
                break;
            case 'BidProcess':
                sortedFilteredData = filteredData.sort(
                    dynamicSort(`${dynamicSortOrder}bidProcessType`),
                );
                break;
            case 'ScheduleSource':
                sortedFilteredData = filteredData.sort(
                    dynamicSort(`${dynamicSortOrder}scheduleSource`),
                );
                break;
            case 'People':
                sortedFilteredData = filteredData.sort(
                    dynamicSort(`${dynamicSortOrder}hyperfindLabel`),
                );
                break;
            case 'Request Period':
                sortedFilteredData = filteredData.sort(
                    nullCheckingDateRangeSort('requestPeriodStartDate', 'requestPeriodEndDate'),
                );
                break;
            case 'Start Date':
                sortedFilteredData = filteredData.sort(
                    nullCheckingDateSort('submissionPeriodStartDate'),
                );
                break;
            case 'End Date':
                sortedFilteredData = filteredData.sort(
                    nullCheckingDateSort('submissionPeriodEndDate'),
                );
                break;
            default:
                break;
        }
        setFilteredData(_.cloneDeep(sortedFilteredData));
    };

    /**
     * @param {moment.Moment|null} date
     * @returns {string}
     */
    useEffect(() => {
        if (!isAppDataLoaded) {
            dispatch(fetchManagerAppData());
        }
    }, []);

    useEffect(() => {
        handleSort(sortValues.sortColumn, sortValues.sortType);
    }, [JSON.stringify(sortValues)]);

    useEffect(() => {
        handleFilter();
    }, [searchValue, JSON.stringify(statusFilterSelected), JSON.stringify(bidProfiles), JSON.stringify(bidProfileDrafts)]);

    useEffect(() => {
        if (managerAppData.isAppDataLoaded) {
            isCreateDraftActionDisabled.current = managerAppData.isCreateDraftActionProcessing;

            const interval = setInterval(() => {
                dispatch(fetchPartialManagerAppData(managerAppData));
            }, 1000 * 60);

            // Clear interval when component unmount
            return () => {
                clearInterval(interval);
            };
        }
        return undefined;
    }, [JSON.stringify(managerAppData)]);

    return (
        <>
            <AppBar
                pageName={pageName}
                pageSelected={ManagerPage.BiddingList}
            />
            <div className="bid-list-full-page">
                <div className="header-container">
                    <div>
                        <Tooltip title={hasManagerBidSetups ? '' : 'No available setups to create a bid'}>
                            <span>
                                <Button
                                    color="primary"
                                    variant="contained"
                                    onClick={handleCreateNewBid}
                                    className="create-bid-button"
                                    disabled={!hasManagerBidSetups || isCreateDraftActionDisabled.current}
                                >
                                    <FontAwesomeIcon icon={faPlusCircle} />
                                    <Typography>{t('createNewBid')}</Typography>
                                </Button>
                            </span>
                        </Tooltip>
                    </div>
                    <FilterSelectionBar
                        setStatusFilterSelected={setStatusFilterSelected}
                        searchValue={searchValue}
                        setSearchValue={setSearchValue}
                        allStatus={allStatus}
                        statusFilterSelected={statusFilterSelected}
                    />
                </div>
                <Paper
                    elevation={2}
                    className="bid-profile-table-container"
                    ref={ref}
                >
                    <Table
                        sortColumn={sortValues.sortColumn}
                        sortType={sortValues.sortType}
                        onSortColumn={(sortColumn, sortType) => setSortValues({ sortColumn, sortType })}
                        height={height}
                        loading={!isAppDataLoaded}
                        data={formatData(filteredData)}
                        id="bid-profile-table"
                        className="table-section"
                        rowClassName="bid-profile-table-row"
                        rowKey={rowKey}
                        isTree
                        shouldUpdateScroll={false}
                    >
                        <Column width={32}>
                            <HeaderCell> </HeaderCell>
                            <Cell />
                        </Column>
                        <Column
                            flexGrow={3}
                            sortable
                            key="name"
                        >
                            <HeaderCell>{t('tableHeaders.name')}</HeaderCell>
                            {
                                // @ts-ignore
                                <BiddingListNameCell
                                    dataKey="Name"
                                    handleNameClick={handleEdit}
                                    defaultUnnamedDraftLabel={t('defaultUnnamedDraftLabel')}
                                    iconTooltipLabel={t('tooltip.parent')}
                                />
                            }
                        </Column>
                        <Column
                            flexGrow={2}
                            sortable
                        >
                            <HeaderCell>{t('tableHeaders.description')}</HeaderCell>
                            <BiddingListTooltipCell
                                dataKey="Description"
                                getValue={(rowData) => rowData.description}
                            />
                        </Column>
                        <Column
                            flexGrow={2}
                            sortable
                        >
                            <HeaderCell>{t('tableHeaders.bidProcess')}</HeaderCell>
                            <BiddingListTooltipCell
                                dataKey="BidProcess"
                                getValue={(rowData) => getBidProcessTypeLabel(rowData.bidProcessType)}
                            />
                        </Column>
                        <Column
                            flexGrow={2}
                            minWidth={140}
                            sortable
                        >
                            <HeaderCell>{t('tableHeaders.scheduleSource')}</HeaderCell>
                            <BiddingListTooltipCell
                                dataKey="ScheduleSource"
                                getValue={(rowData) => getScheduleSourceLabel(rowData.scheduleSource)}
                            />
                        </Column>
                        <Column
                            flexGrow={2}
                            sortable
                        >
                            <HeaderCell>{t('tableHeaders.people')}</HeaderCell>
                            <BiddingListTooltipCell
                                dataKey="People"
                                getValue={(rowData) => rowData.hyperfindLabel}
                            />
                        </Column>
                        <Column
                            flexGrow={3}
                            sortable
                        >
                            <HeaderCell>{t('tableHeaders.requestPeriod')}</HeaderCell>
                            <Cell
                                dataKey="Request Period"
                                style={{ cursor: 'default' }}
                            >
                                {(/** @type {import('rsuite/esm/Table').RowDataType<BidProfile.IBriefBaseProfile>} */rowData) =>
                                    getRequestPeriodLabel(rowData, t('anytime'))}
                            </Cell>
                        </Column>
                        <Column
                            flexGrow={3}
                            sortable
                        >
                            <HeaderCell>{t('tableHeaders.startDateTime')}</HeaderCell>
                            <Cell
                                dataKey="Start Date"
                                style={{ cursor: 'default' }}
                            >
                                {(/** @type {import('rsuite/esm/Table').RowDataType<BidProfile.IBriefBaseProfile>} */rowData) =>
                                    getSubmissionDateLabel(rowData.submissionPeriodStartDate)}
                            </Cell>
                        </Column>
                        <Column
                            flexGrow={2}
                            minWidth={150}
                            sortable
                        >
                            <HeaderCell>{t('tableHeaders.endDateTime')}</HeaderCell>
                            <Cell
                                dataKey="End Date"
                                style={{ cursor: 'default' }}
                            >
                                {(/** @type {import('rsuite/esm/Table').RowDataType<BidProfile.IBriefBaseProfile>} */rowData) =>
                                    getSubmissionDateLabel(rowData.submissionPeriodEndDate)}
                            </Cell>
                        </Column>
                        <Column
                            minWidth={260}
                            flexGrow={4}
                        >
                            <HeaderCell> </HeaderCell>
                            <Cell>
                                {(/** @type {import('rsuite/esm/Table').RowDataType<BidProfile.IBriefBaseProfile>} */rowData) => (
                                    <div
                                        className="tablerow-action-button-container"
                                    >
                                        {
                                            hasRebidButton(rowData.bidProcessType, rowData.bidSetupId) && (
                                                <ButtonWithTooltip
                                                    onClick={() => handleRebid(rowData)}
                                                    disabled={!hasRebidAccess(rowData)}
                                                    tooltipLabel={t('rowActions.rebid')}
                                                >
                                                    <MdiIcon
                                                        path={mdiContentDuplicate}
                                                        size={0.75}
                                                    />
                                                    {t('rowActions.rebid')}
                                                </ButtonWithTooltip>
                                            )
                                        }
                                        <ButtonWithTooltip
                                            onClick={() => handleEdit(rowData)}
                                            disabled={(!hasCreationAccess(rowData) && !hasManagementAccess(rowData) && !hasViewAccess(rowData))}
                                            tooltipLabel={(isReadOnly(rowData.status) || (!rowData.isCreationAllowed && hasViewAccess(rowData)))
                                                ? t('rowActions.view')
                                                : t('rowActions.edit')}
                                        >
                                            {(isReadOnly(rowData.status) || (!rowData.isCreationAllowed && hasViewAccess(rowData)))
                                                ? (
                                                    <>
                                                        <Visibility fontSize="inherit" />
                                                    </>
                                                )
                                                : (
                                                    <>
                                                        <Edit fontSize="inherit" />
                                                    </>
                                                )}
                                        </ButtonWithTooltip>
                                        <ButtonWithTooltip
                                            onClick={() => handleDuplicate(rowData)}
                                            disabled={!hasCreationAccess(rowData)}
                                            tooltipLabel={t('rowActions.duplicate')}
                                        >
                                            <FileCopy fontSize="inherit" />
                                        </ButtonWithTooltip>
                                        <ButtonWithTooltip
                                            onClick={() => handleDelete(rowData)}
                                            disabled={!hasCreationAccess(rowData)}
                                            tooltipLabel={t('rowActions.delete')}
                                        >
                                            <DeleteForever fontSize="inherit" />
                                        </ButtonWithTooltip>
                                        {
                                            (isArchivable(rowData) && hasCreationAccess(rowData)
                                                && ('children' in rowData)
                                            ) && (
                                                <ButtonWithTooltip
                                                    onClick={() => handleArchive(rowData)}
                                                    tooltipLabel={t('rowActions.archive')}
                                                >
                                                    <FontAwesomeIcon icon={faArchive} size="sm" />
                                                </ButtonWithTooltip>
                                            )
                                        }
                                    </div>
                                )}
                            </Cell>
                        </Column>
                    </Table>
                </Paper>
            </div>

        </>
    );
}

export default BiddingList;
